import * as React from 'react';
import { useRef, useState } from 'react';
import axiosApiInstance from '../../../api/axiosClient';
import { Alert, Button, Checkbox, notification, Tooltip } from 'antd';
import { ModalForm, ProFormInstance } from '@ant-design/pro-components';
import { CloudUploadOutlined } from '@ant-design/icons';
import {
    ProFormCheckbox,
    ProFormDatePicker,
    ProFormGroup,
} from '@ant-design/pro-form';
import { latestDeliveryForPlatform, originalUploadBlockedStatus } from './deliveryStatus';

type Props = {
    upc_ean: string;
    id: string;
    release: API.MusicDistribution.MusicRelease;
};

const isBelieveAudioSpecWarning = (warning: API.MusicDistribution.ValidationError) =>
    warning.group === 'believe' &&
    (warning.field === 'audio_spec' ||
        /16-bit|24-bit|32-bit|48kHz|44\.1kHz|packed PCM/i.test(warning.message || ''));

const isDuplicateUPCWarning = (warning: API.MusicDistribution.ValidationError) =>
    warning.field === 'upc_ean' && /already used/i.test(warning.message || '');

const isDuplicateISRCWarning = (warning: API.MusicDistribution.ValidationError) =>
    warning.field === 'isrc' && /ISRC/i.test(warning.message || '');

const UploadRelease = (props: Props) => {
    const formRef = useRef<ProFormInstance>();
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<Array<string>>([]);
    const [believeWarnings, setBelieveWarnings] = useState<API.MusicDistribution.ValidationError[]>([]);
    const [identifierWarnings, setIdentifierWarnings] = useState<API.MusicDistribution.ValidationError[]>([]);
    const [isrcWarnings, setIsrcWarnings] = useState<API.MusicDistribution.ValidationError[]>([]);
    const [forceReplaceAudio, setForceReplaceAudio] = useState(false);

    const platformBlock: Record<string, 'sent' | 'ingested' | undefined> = {
        youtube: originalUploadBlockedStatus(latestDeliveryForPlatform(props.release, 'youtube')?.status),
        believe: originalUploadBlockedStatus(latestDeliveryForPlatform(props.release, 'believe')?.status),
        spotify: originalUploadBlockedStatus(latestDeliveryForPlatform(props.release, 'spotify')?.status),
    };
    const ingesting = (['youtube', 'believe', 'spotify'] as const).filter((p) => platformBlock[p] === 'sent');
    const alreadyLive = (['youtube', 'believe', 'spotify'] as const).filter((p) => platformBlock[p] === 'ingested');
    const isPlatformDisabled = (platform: string) => {
        if (upcBlocked) return true;
        if (platformBlock[platform] === 'sent') return true;
        if (platformBlock[platform] === 'ingested' && !forceReplaceAudio) return true;
        return false;
    };

    const believeAudioSpecWarnings = believeWarnings.filter(isBelieveAudioSpecWarning);
    const believeSoftWarnings = believeWarnings.filter((w) => !isBelieveAudioSpecWarning(w));
    const upcBlocked = identifierWarnings.some(isDuplicateUPCWarning);

    const loadValidationWarnings = async () => {
        try {
            const response = await axiosApiInstance.get(`/api/products/releases/${props.id}`);
            const release = response.data;
            const warnings: API.MusicDistribution.ValidationError[] = [
                ...(release.validation_warnings || []),
                ...((release.art_tracks || []).flatMap(
                    (track: API.MusicDistribution.ArtTrack) => track.validation_warnings || []
                )),
            ];
            const seenDup = new Set<string>();
            const upcDups: API.MusicDistribution.ValidationError[] = [];
            const isrcDups: API.MusicDistribution.ValidationError[] = [];
            for (const warning of warnings.filter(isDuplicateUPCWarning)) {
                const key = warning.message || '';
                if (seenDup.has(key)) continue;
                seenDup.add(key);
                upcDups.push(warning);
            }
            seenDup.clear();
            for (const warning of warnings.filter(isDuplicateISRCWarning)) {
                const key = warning.message || '';
                if (seenDup.has(key)) continue;
                seenDup.add(key);
                isrcDups.push(warning);
            }
            setIdentifierWarnings(upcDups);
            setIsrcWarnings(isrcDups);
            const seen = new Set<string>();
            const deduped: API.MusicDistribution.ValidationError[] = [];
            for (const warning of warnings.filter((w) => w.group === 'believe')) {
                const key = warning.message || '';
                if (seen.has(key)) continue;
                seen.add(key);
                deduped.push(warning);
            }
            setBelieveWarnings(deduped);
        } catch (error) {
            console.error('Failed to load release validation warnings', error);
            setBelieveWarnings([]);
            setIdentifierWarnings([]);
            setIsrcWarnings([]);
        }
    };

    function formatTitle(release: API.MusicDistribution.MusicRelease): string {
        const name = release.name;
        if (release.subtitle) {
            return name + ` (${release.subtitle})`;
        }
        return name;
    }

    return (
        <ModalForm
            formRef={formRef}
            width={600}
            title={`Upload Release: ${formatTitle(props.release)}`}
            trigger={
                <Tooltip title={'Upload Release...'}>
                    <Button type={'link'} icon={<CloudUploadOutlined />} />
                </Tooltip>
            }
            autoFocusFirstInput
            onOpenChange={(open) => {
                if (open) {
                    void loadValidationWarnings();
                } else {
                    setSelected([]);
                    setBelieveWarnings([]);
                    setIdentifierWarnings([]);
                    setIsrcWarnings([]);
                    setForceReplaceAudio(false);
                }
            }}
            modalProps={{
                destroyOnClose: true,
                closable: !loading,
                maskClosable: !loading,
                okText: 'Upload',
                cancelText: 'Cancel',
            }}
            onFinish={async (params) => {
                if (upcBlocked) {
                    notification.error({
                        message: 'Cannot upload',
                        description:
                            'This UPC is already used on another release. Assign a new UPC before uploading.',
                        duration: 8,
                    });
                    return false;
                }
                setLoading(true);
                const uploadTo = (params.upload_to || []) as string[];
                const needsOverride = uploadTo.some((p) => platformBlock[p] === 'ingested');
                setSelected([]);
                return axiosApiInstance
                    .put(`/api/products/releases/${props.id}/upload`, undefined, {
                        params: {
                            ...params,
                            notify_on_failure: params.notify_on_failure !== false,
                            ...(needsOverride && forceReplaceAudio ? { confirm_override: true } : {}),
                        },
                    })
                    .then(() => {
                        setLoading(false);
                        notification.success({
                            message: 'Success',
                            description: 'Upload scheduled successfully'
                        });
                        return true;
                    })
                    .catch((reason) => {
                        setLoading(false);
                        if (reason?.response?.status === 409) {
                            notification.error({
                                message: 'Already delivered',
                                description:
                                    reason?.response?.data?.message ||
                                    'This release is already ingested or still ingesting. Use Modify for metadata.',
                                duration: 10,
                            });
                            return false;
                        }
                        const dupViolations =
                            reason?.response?.data?.duplicate_identifiers_check?.violations;
                        if (dupViolations) {
                            notification.error({
                                message: 'UPC already used',
                                description: Object.values(dupViolations).join(' '),
                                duration: 8,
                            });
                            return false;
                        }
                        const bitDepthViolations =
                            reason?.response?.data?.believe_audio_bit_depth_check?.violations;
                        if (bitDepthViolations) {
                            notification.warning({
                                message: 'Believe audio warning',
                                description: Object.values(bitDepthViolations).join(' '),
                                duration: 8,
                            });
                        }
                        const deliveryErrors = reason?.response?.data?.errors;
                        if (Array.isArray(deliveryErrors) && deliveryErrors.length > 0) {
                            notification.error({
                                message: 'Upload refused',
                                description: deliveryErrors.join(' '),
                                duration: 10,
                            });
                            return false;
                        }
                        const violations = reason?.response?.data?.believe_artist_ids_check?.violations;
                        if (violations) {
                            notification.error({
                                message: 'Believe artist ID validation failed',
                                description: Object.values(violations).join(' '),
                                duration: 8,
                            });
                        }
                        return false;
                    });
            }}
            submitter={{
                submitButtonProps: {
                    disabled: upcBlocked,
                },
            }}
        >
            <ProFormCheckbox.Group
                name='upload_to'
                label='Upload to'
                required
                requiredMark={false}
                fieldProps={{
                    onChange(checkedValue) {
                        setSelected(checkedValue as Array<string>);
                    }
                }}
                options={[
                    {
                        label: 'YouTube',
                        value: 'youtube',
                        disabled: isPlatformDisabled('youtube'),
                    },
                    {
                        label: 'Believe',
                        value: 'believe',
                        disabled: isPlatformDisabled('believe'),
                    },
                    {
                        label: 'Spotify',
                        value: 'spotify',
                        disabled: isPlatformDisabled('spotify'),
                    }
                ]}
            />
            {ingesting.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Still ingesting — do not upload again"
                    description={`${ingesting.join(', ')} already received this release and has not finished. Wait for ingested or failed. Use Modify only after it is live.`}
                />
            )}
            {alreadyLive.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Already ingested"
                    description={
                        <div>
                            <p style={{ marginBottom: 8 }}>
                                {alreadyLive.join(', ')} already accepted this release. Use Modify for
                                metadata. Tick below only if you must replace audio (sends a second original).
                            </p>
                            <Checkbox
                                checked={forceReplaceAudio}
                                onChange={(e) => setForceReplaceAudio(e.target.checked)}
                            >
                                Replace audio on {alreadyLive.join(', ')}
                            </Checkbox>
                        </div>
                    }
                />
            )}
            {upcBlocked && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Cannot upload — UPC already used"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {identifierWarnings.map((warning, index) => (
                                <li key={index}>{warning.message}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {isrcWarnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="ISRC also used on another release"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {isrcWarnings.map((warning, index) => (
                                <li key={index}>{warning.message}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {believeAudioSpecWarnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Believe audio: 16-bit at 44.1/48kHz, or packed 24-bit at 48kHz"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {believeAudioSpecWarnings.map((warning, index) => (
                                <li key={index}>{warning.message}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            {selected.includes('believe') && believeSoftWarnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`${believeSoftWarnings.length} Believe validation warning${believeSoftWarnings.length === 1 ? '' : 's'}`}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {believeSoftWarnings.map((warning, index) => (
                                <li key={index}>{warning.message}</li>
                            ))}
                        </ul>
                    }
                />
            )}
            <ProFormGroup title='Release Dates'>
                <ProFormDatePicker
                    name='custom_release_date_youtube'
                    label='YouTube Music'
                    initialValue={props.release.digital_release_date}
                    normalize={(value) => value?.format('YYYY-MM-DD[T]00:00:00[Z]')}
                    disabled={!selected.includes('youtube')}
                />
                <ProFormDatePicker
                    name='custom_release_date_believe'
                    label='Believe'
                    initialValue={props.release.digital_release_date}
                    normalize={(value) => value?.format('YYYY-MM-DD[T]00:00:00[Z]')}
                    disabled={!selected.includes('believe')}
                />
                <ProFormDatePicker
                    name='custom_release_date_spotify'
                    label='Spotify'
                    initialValue={props.release.digital_release_date}
                    normalize={(value) => value?.format('YYYY-MM-DD[T]00:00:00[Z]')}
                    disabled={!selected.includes('spotify')}
                />
            </ProFormGroup>
            <ProFormCheckbox
                name='notify_on_failure'
                initialValue={true}
                tooltip='If the upload fails (transport or distributor rejection), send an in-app notification to you.'
            >
                Notify me in the portal if this upload fails
            </ProFormCheckbox>
            <ProFormCheckbox
                name='test_delivery'
                tooltip='YouTube & Spotify validate the delivery but do not publish it (MessageControlType=TestMessage). Believe has no test mode and ignores this.'
            >
                Send as test delivery (YouTube / Spotify — validate, don't publish)
            </ProFormCheckbox>
        </ModalForm>
    );
};

export default UploadRelease;
